/**
 * Chat — module-wide constants. Feature-specific constants live next to
 * their feature (e.g. `lib/markdown/constants.ts`, `components/PdfViewer/
 * constants.ts`); this file is for values shared across the module.
 */

import { ReactComponent as NanoBananaIcon } from 'src/assets/models/nano-banana.svg';
import { BASE_SERVER_URL } from 'src/config';
import { NAVIGATIONS } from 'src/utils/constants';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import { IMAGE_GEN_TOOL, KNOWLEDGE_TYPE } from './enums';

/**
 * Prefix of a CLIENT session key — the registry key a brand-new chat streams under
 * before the server has assigned it an id. Never sent to the BE.
 */
export const CLIENT_SESSION_KEY_PREFIX = 'new-';

export const NEW_CHAT_ROUTE = 'new';

/**
 * The standard chat surface. Navigating here with no `:chatId` opens a new chat.
 */
export const CHAT_ROUTE = NAVIGATIONS.CHAT;

/** Build a `/chat[/:chatId]` path. Omit `chatId` for a new chat. */
export const buildChatPath = (chatId?: number | string): string =>
  `${CHAT_ROUTE}${chatId !== undefined && chatId !== null ? `/${chatId}` : ''}`;

/**
 * The canonical new-chat URL (`/chat/new`). Every "open a fresh chat" entry point
 * navigates here, so the sidebar's "New chat" item, which links to the same path,
 * is highlighted whichever way the user arrived.
 */
export const NEW_CHAT_PATH = buildChatPath(NEW_CHAT_ROUTE);

/**
 * Knowledge type a brand-new chat starts in.
 */
export const DEFAULT_KNOWLEDGE_TYPE: KNOWLEDGE_TYPE = KNOWLEDGE_TYPE.WORLD;

/** `r_type` the BE expects when sharing a chat as a public resource. */
export const SHARE_RESOURCE_TYPE_CHAT = 'CHAT';

/** Public route a shared chat resolves to (`<origin>/share-chat/<shareId>`),
 * rendered read-only by `adapters/SharedChatView`. */
export const SHARE_CHAT_PATH = 'share-chat';

/** Build the public share URL from the `share_id` the BE returns. */
export const buildShareChatLink = (shareId: string): string =>
  `${BASE_SERVER_URL}/${SHARE_CHAT_PATH}/${shareId}`;

/** URL segment for the project-chat adapter (`ProjectChatView`). The route is
 * NESTED under the project (`/projects/:projectId/chat/...`), so the chat lives
 * inside the project's own URL space, not a sibling top-level path. */
export const PROJECT_CHAT_SEGMENT = 'chat';

/** Build a `/projects/:projectId/chat[/:chatId]` path. Omit `chatId` (or pass
 * the `new` sentinel) for the new-chat surface. Used by the project "Start chat"
 * entry points, the project chat list, and the adapter's on-create URL rewrite. */
export const buildProjectChatPath = (
  projectId: number | string,
  chatId?: number | string
): string =>
  `${NAVIGATIONS.PROJECTS}/${projectId}/${PROJECT_CHAT_SEGMENT}${
    chatId !== undefined && chatId !== null ? `/${chatId}` : ''
  }`;

/** URL segment for the data-analyst adapter (`DataAnalystChatView`). */
export const DATA_ANALYST_CHAT_SEGMENT = 'chat';

/**
 * Build an `/apps/data-analyst/chat[/:chatId]` path. Omit `chatId` for a new
 * analysis. Used by the Data Analyst page's button, the analysis list's row click,
 * and the adapter's on-create URL rewrite.
 */
export const buildDataAnalystChatPath = (chatId?: number | string): string =>
  `/apps/data-analyst/${DATA_ANALYST_CHAT_SEGMENT}${
    chatId !== undefined && chatId !== null ? `/${chatId}` : ''
  }`;

/** URL segment for the ChatV2 assistant-chat adapter (`AssistantChatView`). The
 * route is NESTED under the assistant (`/assistants/:assistantId/chat/...`),
 * like the project-chat nesting, so the assistant id is part of the address
 * rather than Redux state and a refresh recovers which assistant you were
 * talking to. */
export const ASSISTANT_CHAT_SEGMENT = 'chat';

/** Build an `/assistants/:assistantId/chat/:chatId` path. Pass the `new`
 * sentinel (or omit `chatId`) for the welcome surface. Used by the assistant
 * card, the header's "New chat" action, the sidebar's assistant rows, and the
 * adapter's on-create URL rewrite. */
export const buildAssistantChatPath = (
  assistantId: number | string,
  chatId?: number | string
): string =>
  `${NAVIGATIONS.ASSISTANTS}/${assistantId}/${ASSISTANT_CHAT_SEGMENT}${
    chatId !== undefined && chatId !== null ? `/${chatId}` : ''
  }`;

/**
 * Locales the microphone can transcribe, offered to Azure as an auto-detect set rather
 * than a fixed recognition language — so a German speaker is transcribed as German
 * without changing any setting. Chat-owned: adding a locale here is the whole change.
 */
export const SPEECH_LANGUAGES = ['en-US', 'de-DE', 'tr-TR'];

/** Max attachments per message. */
export const MAX_ATTACHMENTS = 20;

/**
 * How long a turn may go without a single wire frame before the
 * [StreamRunner](./stream/streamRunner.ts) watchdog declares it dead.
 */
export const STREAM_IDLE_TIMEOUT_MS = 360_000;

/**
 * Extension allowlist for chat attachments (dot-less, lowercase — the shape
 * `validateUploadFiles` expects).
 */
export const CHAT_ALLOWED_EXTENSIONS = [
  'docx',
  'doc',
  'pptx',
  'ppt',
  'xlsx',
  'xls',
  'csv',
  'odt',
  'odp',
  'ods',
  'pdf',
  'dot',
  'rtf',
  'mp3',
  'mp4',
  'mpga',
  'mpeg',
  'wav',
  'm4a',
  'webm',
  'txt',
  'png',
  'jpeg',
  'jpg',
  'gif',
  'svg'
];

/**
 * Dot-less extensions → an `accept=""` string for `<input type="file">`.
 */
export const toAcceptAttribute = (extensions: readonly string[]): string =>
  extensions.map((ext) => `.${ext}`).join(',');

/**
 * Attachment allowlist for the DATA ANALYST mode — spreadsheets only.
 */
export const DATA_ANALYST_ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv'];

/**
 * What the data analyst may SELECT from the enterprise knowledge base — the
 * hybrid-search picker's format filter, sent as `filter_by_file_format`.
 */
export const DATA_ANALYST_SELECTABLE_EXTENSIONS = ['xlsx', 'xls'];

const PROCESSING_EXEMPT_EXTENSIONS = new Set([
  'xls',
  'xlsx',
  'csv',
  'txt',
  'png',
  'jpg',
  'jpeg',
  'gif'
]);

/** True when `file` skips BE indexing (see `PROCESSING_EXEMPT_EXTENSIONS`). */
export const isProcessingExempt = (file: File | { name: string }): boolean =>
  PROCESSING_EXEMPT_EXTENSIONS.has(
    file.name.split('.').pop()?.toLowerCase() ?? ''
  );
/** The `format` values the backend uses for a web-search result (it has sent both). */
export const WEB_SOURCE_FORMATS: ReadonlySet<string> = new Set(['web', 'url']);

/** Is this source `format` a web-search result rather than a file? */
export const isWebSourceFormat = (format: unknown): boolean =>
  typeof format === 'string' && WEB_SOURCE_FORMATS.has(format);

/** The app's not-found page — where a chat the backend cannot find is sent. */
export const NOT_FOUND_ROUTE = '/404';

/** Route for the compare-models surface (`CompareChatView`), a sibling top-level path. */
export const COMPARE_CHAT_PATH = 'compare-models-chat';

export const TOOLBAR_IMAGE_GENERATION_MODELS: {
  id: IMAGE_GEN_TOOL;
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}[] = [
  {
    id: IMAGE_GEN_TOOL.NANO_BANANA,
    title: 'Nano Banana',
    description: T.nanoBananaDescription,
    icon: NanoBananaIcon
  }
];
